import React, { useState, useEffect, useCallback } from "react";
import {
  getStatsSummary,
  getDailyStats,
  getMonthlyStats,
  getYearlyStats,
  getPageStats,
  exportStatsJson,
  getAuditHistory,
} from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const StatsBoard = ({ adminId }) => {
  const [summary, setSummary] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [yearlyStats, setYearlyStats] = useState([]);
  const [pageStats, setPageStats] = useState([]);
  const [adminActivityLogs, setAdminActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [activeTab, setActiveTab] = useState("summary");
  const [language, setLanguage] = useState("en");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const translations = {
    en: {
      title: "Website Statistics Dashboard",
      loading: "Loading statistics...",
      summary: "Summary",
      daily: "Daily",
      monthly: "Monthly",
      yearly: "Yearly",
      topPages: "Top Pages",
      adminActivity: "Admin Activity",
      export: "Export JSON",
      todayVisits: "Today's Visits",
      uniqueVisitors: "Unique Visitors",
      pageViews: "Page Views",
      sessions: "Sessions",
      date: "Date",
      visitors: "Visitors",
      views: "Views",
      pagePerSession: "Pages / Session",
      totalSessions: "Total Sessions",
      firstVisit: "First Visit",
      lastVisit: "Last Visit",
      pageShare: "View Share (%)",
      year: "Year",
      month: "Month",
      page: "Page",
      totalVisitors: "Total Visitors",
      from: "From",
      to: "To",
      search: "Search",
      sevenDayAverage: "7-Day Average",
      allTimeTotal: "All-Time Total",
      exportSuccess: "Statistics exported successfully",
      exportFailed: "Failed to export statistics",
      admin: "Admin",
      action: "Action",
      recordType: "Record Type",
      timestamp: "Timestamp",
      details: "Details",
      viewer: "Viewers",
      field: "Field",
      beforeValue: "Before",
      afterValue: "After",
      changedFields: "Changed Fields",
      createdFields: "Created Fields",
      deletedFields: "Deleted Fields",
      rawDescription: "Raw Description",
      showFull: "Show full",
      auditEvent: "Audit Event",
      summaryInsights: "Summary Insights",
      trafficTrend: "Traffic Trend (Last 14 Days)",
      topPageDistribution: "Top Page Distribution",
      visitorsTrend: "Visitors",
      viewsTrend: "Page Views",
      sessionsTrend: "Sessions",
      noChartData: "No chart data yet",
    },
    vi: {
      title: "Bảng Điều Khiển Thống Kê Website",
      loading: "Đang tải thống kê...",
      summary: "Tóm Tắt",
      daily: "Theo Ngày",
      monthly: "Theo Tháng",
      yearly: "Theo Năm",
      topPages: "Trang Phổ Biến",
      adminActivity: "Hoạt Động Admin",
      export: "Xuất JSON",
      todayVisits: "Lượt Truy Cập Hôm Nay",
      uniqueVisitors: "Khách Duy Nhất",
      pageViews: "Lượt Xem",
      sessions: "Phiên",
      date: "Ngày",
      visitors: "Khách",
      views: "Lượt",
      pagePerSession: "Trang / Phiên",
      totalSessions: "Tổng Phiên",
      firstVisit: "Lần Đầu",
      lastVisit: "Gần Nhất",
      pageShare: "% Lượt Xem Trên Tổng",
      year: "Năm",
      month: "Tháng",
      page: "Trang",
      totalVisitors: "Tổng Khách",
      from: "Từ",
      to: "Đến",
      search: "Tìm Kiếm",
      sevenDayAverage: "Trung Bình 7 Ngày",
      allTimeTotal: "Tổng Số Tất Cả",
      exportSuccess: "Xuất thống kê thành công",
      exportFailed: "Không thể xuất thống kê",
      admin: "Admin",
      action: "Hành Động",
      recordType: "Loại Dữ Liệu",
      timestamp: "Thời Gian",
      details: "Chi Tiết",
      viewer: "Lượt Xem",
      field: "Trường Dữ Liệu",
      beforeValue: "Giá Trị Cũ",
      afterValue: "Giá Trị Mới",
      changedFields: "Các Trường Đã Thay Đổi",
      createdFields: "Các Trường Đã Tạo",
      deletedFields: "Dữ Liệu Đã Xóa",
      rawDescription: "Mô Tả Gốc",
      showFull: "Xem đầy đủ",
      auditEvent: "Sự Kiện Audit",
      summaryInsights: "Phân Tích Tổng Quan",
      trafficTrend: "Xu Hướng Truy Cập (14 Ngày Gần Nhất)",
      topPageDistribution: "Phân Bổ Theo Từng Trang",
      visitorsTrend: "Khách",
      viewsTrend: "Lượt Xem",
      sessionsTrend: "Phiên",
      noChartData: "Chưa có dữ liệu biểu đồ",
    },
  };

  const t = translations[language] || translations.en;

  const fetchAllStats = useCallback(async () => {
    setLoading(true);
    try {
      const [summ, daily, monthly, yearly, pages] = await Promise.all([
        getStatsSummary(),
        getDailyStats(),
        getMonthlyStats(),
        getYearlyStats(),
        getPageStats(),
      ]);

      setSummary(summ);
      setDailyStats(daily);
      setMonthlyStats(monthly);
      setYearlyStats(yearly);
      setPageStats(pages);
    } catch (err) {
      setToast({ message: "Failed to load statistics", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAdminActivity = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      const logs = await getAuditHistory(filters);
      setAdminActivityLogs(logs);
    } catch (err) {
      setToast({ message: "Failed to load admin activity", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      fetchAllStats();
    } else {
      setLoading(false);
      setToast({ message: "No authentication token found", type: "error" });
    }
  }, [fetchAllStats]);

  useEffect(() => {
    if (activeTab === "adminActivity") {
      fetchAdminActivity();
    }
  }, [activeTab, fetchAdminActivity]);

  const handleExport = async () => {
    try {
      await exportStatsJson(dateRange.startDate, dateRange.endDate);
      setToast({ message: t.exportSuccess, type: "success" });
    } catch (err) {
      setToast({ message: err.message || t.exportFailed, type: "error" });
    }
  };

  const safeToFixed = (value, digits = 0) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(digits) : "0";
  };

  const normalizeDayKey = (inputDate) => {
    const date = new Date(inputDate);
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const buildTrendData = (stats, currentLanguage) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const statsByDay = new Map();
    (Array.isArray(stats) ? stats : []).forEach((item) => {
      const dayKey = normalizeDayKey(item.visit_date);
      if (!dayKey) return;
      statsByDay.set(dayKey, {
        visitors: Number(item.unique_visitors || 0),
        views: Number(item.page_views || 0),
        sessions: Number(item.sessions || 0),
      });
    });

    return Array.from({ length: 14 }, (_, offset) => {
      const date = new Date(now);
      date.setDate(now.getDate() - (13 - offset));
      const dayKey = normalizeDayKey(date);
      const values = statsByDay.get(dayKey) || { visitors: 0, views: 0, sessions: 0 };

      return {
        label: date.toLocaleDateString(currentLanguage === "vi" ? "vi-VN" : "en-US", {
          month: "short",
          day: "2-digit",
        }),
        visitors: values.visitors,
        views: values.views,
        sessions: values.sessions,
      };
    });
  };

  const parseKeyValueLines = (text) => {
    if (!text) return [];
    return text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const separatorIndex = line.indexOf(":");
        if (separatorIndex <= 0) return null;
        const field = line.slice(0, separatorIndex).trim();
        const value = line.slice(separatorIndex + 1).trim();
        return { field, value: value || "(empty)" };
      })
      .filter(Boolean);
  };

  const tryParseJson = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value !== "string") return null;
    const trimmedValue = value.trim();
    if (!trimmedValue || (!trimmedValue.startsWith("{") && !trimmedValue.startsWith("["))) {
      return null;
    }
    try {
      return JSON.parse(trimmedValue);
    } catch {
      return null;
    }
  };

  const flattenObject = (data, parentKey = "") => {
    if (data === null || data === undefined) {
      return [{ key: parentKey || "value", value: data }];
    }

    if (typeof data !== "object") {
      return [{ key: parentKey || "value", value: data }];
    }

    if (Array.isArray(data)) {
      if (data.length === 0) {
        return [{ key: parentKey || "value", value: [] }];
      }
      return data.flatMap((item, index) => flattenObject(item, `${parentKey}[${index}]`));
    }

    const entries = Object.entries(data);
    if (entries.length === 0) {
      return [{ key: parentKey || "value", value: {} }];
    }

    return entries.flatMap(([key, value]) => {
      const nextKey = parentKey ? `${parentKey}.${key}` : key;
      if (value !== null && typeof value === "object") {
        return flattenObject(value, nextKey);
      }
      return [{ key: nextKey, value }];
    });
  };

  const toComparableString = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    return JSON.stringify(value);
  };

  const expandJsonRowDiff = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;

    const expandedRows = [];

    rows.forEach((row) => {
      const beforeJson = tryParseJson(row.before);
      const afterJson = tryParseJson(row.after);

      if (!beforeJson || !afterJson || Array.isArray(beforeJson) || Array.isArray(afterJson)) {
        expandedRows.push(row);
        return;
      }

      const flatBefore = flattenObject(beforeJson, row.field);
      const flatAfter = flattenObject(afterJson, row.field);

      const keySet = new Set([...flatBefore.map((item) => item.key), ...flatAfter.map((item) => item.key)]);
      const beforeMap = new Map(flatBefore.map((item) => [item.key, item.value]));
      const afterMap = new Map(flatAfter.map((item) => [item.key, item.value]));

      const nestedRows = Array.from(keySet)
        .sort((leftKey, rightKey) => leftKey.localeCompare(rightKey))
        .map((key) => {
          const beforeValue = beforeMap.has(key) ? beforeMap.get(key) : "(empty)";
          const afterValue = afterMap.has(key) ? afterMap.get(key) : "(empty)";
          return {
            field: key,
            before: typeof beforeValue === "string" ? beforeValue : JSON.stringify(beforeValue),
            after: typeof afterValue === "string" ? afterValue : JSON.stringify(afterValue),
          };
        })
        .filter((item) => toComparableString(item.before) !== toComparableString(item.after));

      if (nestedRows.length > 0) {
        expandedRows.push(...nestedRows);
      } else {
        expandedRows.push(row);
      }
    });

    return expandedRows;
  };

  const parseAuditDiff = (descriptionText) => {
    if (!descriptionText || typeof descriptionText !== "string") {
      return { mode: "none", rows: [] };
    }

    if (descriptionText.includes("Changes:")) {
      const changesText = descriptionText.split("Changes:")[1] || "";
      const pattern = /•\s(.+?):\s*\n\s*←\s*(.*?)\s*\n\s*→\s*(.*?)(?=\n\n•|\n•|$)/gs;
      const rows = [];
      let matchResult;

      while ((matchResult = pattern.exec(changesText)) !== null) {
        rows.push({
          field: matchResult[1]?.trim() || "(unknown)",
          before: matchResult[2]?.trim() || "(empty)",
          after: matchResult[3]?.trim() || "(empty)",
        });
      }

      if (rows.length > 0) {
        return { mode: "update", rows: expandJsonRowDiff(rows) };
      }
    }

    if (descriptionText.includes("Fields:")) {
      const fieldsText = descriptionText.split("Fields:")[1] || "";
      const rows = parseKeyValueLines(fieldsText);
      if (rows.length > 0) {
        return { mode: "create", rows };
      }
    }

    if (descriptionText.includes("Deleted data:")) {
      const fieldsText = descriptionText.split("Deleted data:")[1] || "";
      const rows = parseKeyValueLines(fieldsText);
      if (rows.length > 0) {
        return { mode: "delete", rows };
      }
    }

    return { mode: "none", rows: [] };
  };

  const getAuditHeadline = (descriptionText) => {
    if (!descriptionText || typeof descriptionText !== "string") return "";
    const lines = descriptionText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    return lines[0] || "";
  };

  const renderDiffValue = (inputValue) => {
    const normalizedValue = inputValue === null || inputValue === undefined || inputValue === "" ? "(empty)" : String(inputValue);

    let displayValue = normalizedValue;
    const looksLikeJson = normalizedValue.startsWith("{") || normalizedValue.startsWith("[");
    if (looksLikeJson) {
      try {
        displayValue = JSON.stringify(JSON.parse(normalizedValue), null, 2);
      } catch {
        displayValue = normalizedValue;
      }
    }

    const isLongValue = displayValue.length > 220 || displayValue.includes("\n");
    if (!isLongValue) {
      return <span>{displayValue}</span>;
    }

    const preview = `${displayValue.slice(0, 160)}... (${displayValue.length} chars)`;
    return (
      <details>
        <summary style={{ cursor: "pointer", color: "#2563eb", fontWeight: 500 }}>{`${t.showFull} • ${preview}`}</summary>
        <pre
          style={{
            margin: "8px 0 0 0",
            padding: "10px",
            backgroundColor: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "4px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "monospace",
            fontSize: "12px",
            color: "#0f172a",
            lineHeight: "1.45",
          }}
        >
          {displayValue}
        </pre>
      </details>
    );
  };

  const chartPalette = {
    visitors: "#3b82f6",
    views: "#224191",
    sessions: "#10b981",
    bars: "#224191",
  };

  const SummarySection = ({ title, children }) => (
    <div style={{ background: "#fff", borderRadius: "8px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
      <h3 style={{ margin: "0 0 16px 0" }}>{title}</h3>
      {children}
    </div>
  );

  const SimpleLineChart = ({ data, lines, noDataText }) => {
    const [hoverIndex, setHoverIndex] = useState(null);

    if (!Array.isArray(data) || data.length === 0) {
      return <div style={{ color: "#777", padding: "20px 0" }}>{noDataText}</div>;
    }

    const width = 300;
    const height = 100;
    const chartPadding = { top: 10, right: 3, bottom: 10, left: 12 };
    const chartWidth = width - chartPadding.left - chartPadding.right;
    const chartHeight = height - chartPadding.top - chartPadding.bottom;
    const maxValue = Math.max(
      ...data.flatMap((item) =>
        lines.map((line) => {
          const rawValue = Number(item[line.key] || 0);
          return Number.isFinite(rawValue) ? rawValue : 0;
        }),
      ),
      1,
    );

    const buildPoints = (lineKey) =>
      data
        .map((item, index) => {
          const x = data.length === 1 ? chartPadding.left + chartWidth / 2 : chartPadding.left + (index / (data.length - 1)) * chartWidth;
          const yValue = Number(item[lineKey] || 0);
          const normalized = Number.isFinite(yValue) ? yValue / maxValue : 0;
          const y = chartPadding.top + chartHeight - normalized * chartHeight;
          return { x, y };
        })
        .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

    const pointsToPolyline = (points) => points.map((point) => `${point.x},${point.y}`).join(" ");

    const linePointsMap = lines.reduce((accumulator, line) => {
      accumulator[line.key] = buildPoints(line.key);
      return accumulator;
    }, {});

    const yTickValues = [1, 0.75, 0.5, 0.25, 0].map((ratio) => {
      const value = Math.round(maxValue * ratio);
      const y = chartPadding.top + chartHeight * (1 - ratio);
      return { ratio, value, y };
    });

    const hoverReferencePoints = lines.length > 0 ? linePointsMap[lines[0].key] || [] : [];
    const hoverPoint = hoverIndex !== null ? hoverReferencePoints[hoverIndex] : null;
    const hoveredData = hoverIndex !== null ? data[hoverIndex] : null;

    const firstLabel = data[0]?.label || "";
    const middleLabel = data[Math.floor(data.length / 2)]?.label || "";
    const lastLabel = data[data.length - 1]?.label || "";

    return (
      <div style={{ position: "relative" }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: "100%", height: "260px", display: "block", background: "#fafafa", borderRadius: "8px" }}
          onMouseLeave={() => setHoverIndex(null)}
        >
          {yTickValues.map((tick) => (
            <g key={tick.ratio}>
              <line x1={chartPadding.left} y1={tick.y} x2={width - chartPadding.right} y2={tick.y} stroke="#e5e7eb" strokeWidth="0.5" />
              <text x={chartPadding.left - 1.5} y={tick.y + 1.2} textAnchor="end" fontSize="3" fill="#6b7280">
                {tick.value}
              </text>
            </g>
          ))}

          {lines.map((line) => {
            const points = linePointsMap[line.key] || [];
            if (points.length === 0) return null;

            return (
              <g key={line.key}>
                <polyline fill="none" stroke={line.color} strokeWidth="1.8" points={pointsToPolyline(points)} />
                {points.map((point, index) => (
                  <circle key={`${line.key}-${index}`} cx={point.x} cy={point.y} r="1.2" fill={line.color} />
                ))}
              </g>
            );
          })}

          {hoverReferencePoints.map((point, index) => {
            const leftNeighbor = hoverReferencePoints[index - 1];
            const rightNeighbor = hoverReferencePoints[index + 1];
            const leftBoundary = leftNeighbor ? (leftNeighbor.x + point.x) / 2 : chartPadding.left;
            const rightBoundary = rightNeighbor ? (rightNeighbor.x + point.x) / 2 : width - chartPadding.right;
            return (
              <rect
                key={`hover-zone-${index}`}
                x={leftBoundary}
                y={chartPadding.top}
                width={Math.max(rightBoundary - leftBoundary, 0.8)}
                height={chartHeight}
                fill="transparent"
                onMouseEnter={() => setHoverIndex(index)}
              />
            );
          })}

          {hoverPoint && (
            <line
              x1={hoverPoint.x}
              y1={chartPadding.top}
              x2={hoverPoint.x}
              y2={chartPadding.top + chartHeight}
              stroke="#9ca3af"
              strokeDasharray="1.5,1.5"
              strokeWidth="0.5"
            />
          )}
        </svg>

        {hoveredData && hoverPoint && (
          <div
            style={{
              position: "absolute",
              left: `${(hoverPoint.x / width) * 100}%`,
              top: "8px",
              transform: "translateX(-50%)",
              background: "rgba(17, 24, 39, 0.92)",
              color: "#fff",
              borderRadius: "6px",
              padding: "8px 10px",
              fontSize: "12px",
              lineHeight: 1.5,
              pointerEvents: "none",
              minWidth: "140px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>{hoveredData.label}</div>
            {lines.map((line) => (
              <div key={`tooltip-${line.key}`} style={{ display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: line.color, display: "inline-block" }} />
                <span>
                  {line.label}: {Number(hoveredData[line.key] || 0).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", color: "#666", fontSize: "12px", marginTop: "8px" }}>
          <span>{firstLabel}</span>
          <span>{middleLabel}</span>
          <span>{lastLabel}</span>
        </div>

        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "14px" }}>
          {lines.map((line) => (
            <div key={line.key} style={{ display: "flex", alignItems: "center", gap: "8px", color: "#444", fontSize: "13px" }}>
              <span style={{ width: "12px", height: "12px", borderRadius: "50%", background: line.color, display: "inline-block" }} />
              <span>{line.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const HorizontalBarChart = ({ data, valueKey, labelKey, noDataText }) => {
    if (!Array.isArray(data) || data.length === 0) {
      return <div style={{ color: "#777", padding: "20px 0" }}>{noDataText}</div>;
    }

    const maxValue = Math.max(...data.map((item) => Number(item[valueKey] || 0)), 1);

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {data.map((item) => {
          const value = Number(item[valueKey] || 0);
          const percent = (value / maxValue) * 100;
          return (
            <div key={item[labelKey]}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "6px" }}>
                <span style={{ color: "#333", fontWeight: 500 }}>{item[labelKey]}</span>
                <span style={{ color: "#666" }}>{value.toLocaleString()}</span>
              </div>
              <div style={{ background: "#eef2ff", borderRadius: "999px", height: "10px", overflow: "hidden" }}>
                <div style={{ width: `${percent}%`, height: "100%", background: chartPalette.bars }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const StatCard = ({ label, value, unit = "" }) => (
    <div style={{ flex: "1 1 200px", background: "#fff", padding: "20px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
      <p style={{ color: "#666", fontSize: "14px", margin: "0 0 10px 0" }}>{label}</p>
      <p style={{ fontSize: "32px", fontWeight: "bold", color: "#224191", margin: "0" }}>
        {value?.toLocaleString?.() || value || "0"}
        <span style={{ fontSize: "16px", marginLeft: "5px" }}>{unit}</span>
      </p>
    </div>
  );

  if (loading) return <div className="admin-manager-loading">{t.loading}</div>;

  const totalPageViews = pageStats.reduce((sum, stat) => sum + Number(stat.views || 0), 0);
  const trendData = buildTrendData(dailyStats, language);
  const parsedSelectedLogDiff = parseAuditDiff(selectedLog?.description);
  const auditHeadline = getAuditHeadline(selectedLog?.description);
  const filteredDiffRows = parsedSelectedLogDiff.rows.filter((row) => {
    const beforeValue = parsedSelectedLogDiff.mode === "create" ? "-" : row.before || row.value || "(empty)";
    const afterValue = parsedSelectedLogDiff.mode === "delete" ? "-" : row.after || row.value || "(empty)";
    return String(beforeValue).trim() !== String(afterValue).trim();
  });

  const topPagesForChart = [...pageStats]
    .sort((firstItem, secondItem) => Number(secondItem.views || 0) - Number(firstItem.views || 0))
    .slice(0, 8)
    .map((item) => ({
      path: item.page_path,
      views: Number(item.views || 0),
    }));

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <div>
          <h1>{t.title}</h1>
        </div>
        <div>
          <button onClick={() => setLanguage("en")} style={{ marginRight: "10px", fontWeight: language === "en" ? "bold" : "normal" }}>
            English
          </button>
          <button onClick={() => setLanguage("vi")} style={{ fontWeight: language === "vi" ? "bold" : "normal" }}>
            Tiếng Việt
          </button>
        </div>
      </div>

      {/* Export Section */}
      <div style={{ background: "#fff", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
        <h3>{t.export}</h3>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label>{t.from}</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              style={{ display: "block", padding: "8px", marginTop: "5px", borderRadius: "4px", border: "1px solid #ddd" }}
            />
          </div>
          <div>
            <label>{t.to}</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              style={{ display: "block", padding: "8px", marginTop: "5px", borderRadius: "4px", border: "1px solid #ddd" }}
            />
          </div>
          <button className="admin-manager-btn-primary" onClick={handleExport}>
            {t.export}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px", borderBottom: "1px solid #ddd", overflowX: "auto", paddingBottom: "4px" }}>
        {[
          { key: "summary", label: t.summary },
          { key: "daily", label: t.daily },
          { key: "monthly", label: t.monthly },
          { key: "yearly", label: t.yearly },
          { key: "pages", label: t.topPages },
          { key: "adminActivity", label: t.adminActivity },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 20px",
              border: "none",
              background: activeTab === tab.key ? "#224191" : "transparent",
              color: activeTab === tab.key ? "white" : "#666",
              cursor: "pointer",
              borderBottom: activeTab === tab.key ? "3px solid #224191" : "none",
              fontWeight: activeTab === tab.key ? "bold" : "normal",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary Tab */}
      {activeTab === "summary" && summary && (
        <div>
          <SummarySection title={t.summaryInsights}>
            <div style={{ color: "#666", fontSize: "14px" }}>
              {language === "vi"
                ? "Biểu đồ dưới đây giúp theo dõi xu hướng truy cập theo thời gian và mức độ quan tâm theo từng trang."
                : "The charts below show traffic trends over time and interest distribution across pages."}
            </div>
          </SummarySection>

          <h3>{t.todayVisits}</h3>
          <div style={{ display: "flex", gap: "15px", marginBottom: "30px", flexWrap: "wrap" }}>
            <StatCard label={t.uniqueVisitors} value={summary.today?.unique_visitors} />
            <StatCard label={t.pageViews} value={summary.today?.page_views} />
            <StatCard
              label={t.pagePerSession}
              value={
                summary.today?.sessions
                  ? safeToFixed(Number(summary.today?.page_views || 0) / Math.max(Number(summary.today?.sessions || 0), 1), 2)
                  : "0"
              }
            />
          </div>

          <h3>{t.sevenDayAverage}</h3>
          <div style={{ display: "flex", gap: "15px", marginBottom: "30px", flexWrap: "wrap" }}>
            <StatCard label={t.uniqueVisitors} value={safeToFixed(summary.sevenDayAverage?.avg_visitors, 0)} />
            <StatCard label={t.pageViews} value={safeToFixed(summary.sevenDayAverage?.avg_page_views, 0)} />
            <StatCard
              label={t.pagePerSession}
              value={
                Number(summary.sevenDayAverage?.avg_visitors || 0) > 0
                  ? safeToFixed(Number(summary.sevenDayAverage?.avg_page_views || 0) / Number(summary.sevenDayAverage?.avg_visitors || 1), 2)
                  : "0"
              }
            />
          </div>

          <h3>{t.allTimeTotal}</h3>
          <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
            <StatCard label={t.uniqueVisitors} value={summary.totalAllTime?.total_visitors} />
            <StatCard label={t.pageViews} value={summary.totalAllTime?.total_page_views} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "20px", marginTop: "28px" }}>
            <SummarySection title={t.trafficTrend}>
              <SimpleLineChart
                data={trendData}
                noDataText={t.noChartData}
                lines={[
                  { key: "visitors", label: t.visitorsTrend, color: chartPalette.visitors },
                  { key: "views", label: t.viewsTrend, color: chartPalette.views },
                ]}
              />
            </SummarySection>

            <SummarySection title={t.topPageDistribution}>
              <HorizontalBarChart data={topPagesForChart} valueKey="views" labelKey="path" noDataText={t.noChartData} />
            </SummarySection>
          </div>
        </div>
      )}

      {/* Daily Tab */}
      {activeTab === "daily" && (
        <div className="admin-manager-table">
          <table>
            <thead>
              <tr>
                <th>{t.date}</th>
                <th>{t.visitors}</th>
                <th>{t.pageViews}</th>
                <th>{t.sessions}</th>
                <th>{t.pagePerSession}</th>
              </tr>
            </thead>
            <tbody>
              {dailyStats.slice(0, 30).map((stat) => (
                <tr key={stat.visit_date}>
                  <td>{new Date(stat.visit_date).toLocaleDateString()}</td>
                  <td>{stat.unique_visitors}</td>
                  <td>{stat.page_views}</td>
                  <td>{stat.sessions}</td>
                  <td>{safeToFixed(Number(stat.page_views || 0) / Math.max(Number(stat.sessions || 0), 1), 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Monthly Tab */}
      {activeTab === "monthly" && (
        <div className="admin-manager-table">
          <table>
            <thead>
              <tr>
                <th>{t.year}</th>
                <th>{t.month}</th>
                <th>{t.totalVisitors}</th>
                <th>{t.pageViews}</th>
                <th>{t.sessions}</th>
                <th>{t.pagePerSession}</th>
              </tr>
            </thead>
            <tbody>
              {monthlyStats.map((stat) => (
                <tr key={`${stat.year}-${stat.month}`}>
                  <td>{stat.year}</td>
                  <td>{stat.month}</td>
                  <td>{stat.total_unique_visitors}</td>
                  <td>{stat.total_page_views}</td>
                  <td>{stat.total_sessions}</td>
                  <td>{safeToFixed(Number(stat.total_page_views || 0) / Math.max(Number(stat.total_sessions || 0), 1), 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Yearly Tab */}
      {activeTab === "yearly" && (
        <div className="admin-manager-table">
          <table>
            <thead>
              <tr>
                <th>{t.year}</th>
                <th>{t.totalVisitors}</th>
                <th>{t.pageViews}</th>
                <th>{t.sessions}</th>
                <th>{t.pagePerSession}</th>
              </tr>
            </thead>
            <tbody>
              {yearlyStats.map((stat) => (
                <tr key={stat.year}>
                  <td>{stat.year}</td>
                  <td>{stat.total_unique_visitors}</td>
                  <td>{stat.total_page_views}</td>
                  <td>{stat.total_sessions}</td>
                  <td>{safeToFixed(Number(stat.total_page_views || 0) / Math.max(Number(stat.total_sessions || 0), 1), 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pages Tab */}
      {activeTab === "pages" && (
        <div className="admin-manager-table">
          <table>
            <thead>
              <tr>
                <th>{t.page}</th>
                <th>{t.viewer}</th>
                <th>{t.uniqueVisitors}</th>
                <th>{t.totalSessions}</th>
                <th>{t.pagePerSession}</th>
                <th>{t.pageShare}</th>
                <th>{t.firstVisit}</th>
                <th>{t.lastVisit}</th>
              </tr>
            </thead>
            <tbody>
              {pageStats.map((stat) => (
                <tr key={stat.page_path}>
                  <td>{stat.page_path}</td>
                  <td>{stat.views}</td>
                  <td>{stat.unique_visitors}</td>
                  <td>{stat.sessions ?? stat.unique_visitors}</td>
                  <td>{safeToFixed(Number(stat.views || 0) / Math.max(Number(stat.sessions || stat.unique_visitors || 0), 1), 2)}</td>
                  <td>{totalPageViews > 0 ? `${safeToFixed((Number(stat.views || 0) / totalPageViews) * 100, 2)}%` : "0%"}</td>
                  <td>{new Date(stat.first_visit).toLocaleDateString()}</td>
                  <td>{new Date(stat.last_visit).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Admin Activity Tab */}
      {activeTab === "adminActivity" && (
        <div>
          <div
            style={{
              background: "#fff",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              gap: "10px",
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <div>
              <label>{t.from}</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                style={{ display: "block", padding: "8px", marginTop: "5px", borderRadius: "4px", border: "1px solid #ddd" }}
              />
            </div>
            <div>
              <label>{t.to}</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                style={{ display: "block", padding: "8px", marginTop: "5px", borderRadius: "4px", border: "1px solid #ddd" }}
              />
            </div>
            <button className="admin-manager-btn-primary" onClick={fetchAdminActivity}>
              {t.search}
            </button>
          </div>

          <div className="admin-manager-table">
            <table>
              <thead>
                <tr>
                  <th>{t.admin}</th>
                  <th>{t.action}</th>
                  <th>{t.timestamp}</th>
                </tr>
              </thead>
              <tbody>
                {adminActivityLogs.length === 0 ? (
                  <tr>
                    <td colSpan="3" style={{ textAlign: "center", color: "#999", padding: "20px" }}>
                      No activity records found
                    </td>
                  </tr>
                ) : (
                  adminActivityLogs.map((log, idx) => (
                    <tr
                      key={idx}
                      onClick={() => {
                        setSelectedLog(log);
                        setShowDetailsModal(true);
                      }}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                    >
                      <td>{log.full_name || log.username || `Admin ${log.admin_id}`}</td>
                      <td>
                        <span
                          style={{
                            padding: "6px 12px",
                            borderRadius: "4px",
                            fontSize: "13px",
                            fontWeight: "600",
                            display: "inline-block",
                            backgroundColor:
                              log.action_type === "CREATE"
                                ? "#d4edda"
                                : log.action_type === "UPDATE"
                                  ? "#cfe2ff"
                                  : log.action_type === "DELETE"
                                    ? "#f8d7da"
                                    : log.action_type === "LOGIN"
                                      ? "#e7d4f5"
                                      : log.action_type === "PASSWORD_RESET"
                                        ? "#fff3cd"
                                        : log.action_type === "PASSWORD_CHANGE"
                                          ? "#e2e3e5"
                                          : "#f0f0f0",
                            color:
                              log.action_type === "CREATE"
                                ? "#155724"
                                : log.action_type === "UPDATE"
                                  ? "#084298"
                                  : log.action_type === "DELETE"
                                    ? "#721c24"
                                    : log.action_type === "LOGIN"
                                      ? "#531e8a"
                                      : log.action_type === "PASSWORD_RESET"
                                        ? "#856404"
                                        : log.action_type === "PASSWORD_CHANGE"
                                          ? "#383d41"
                                          : "#666",
                          }}
                        >
                          {log.action_type}
                        </span>
                      </td>
                      <td>{new Date(log.created_at).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedLog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "30px",
              maxWidth: "600px",
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, color: "#333" }}>Activity Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "28px",
                  cursor: "pointer",
                  color: "#999",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ fontWeight: "bold", color: "#666" }}>Admin:</label>
              <p style={{ margin: "5px 0 0 0", color: "#333" }}>{selectedLog.full_name || selectedLog.username || `Admin ${selectedLog.admin_id}`}</p>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ fontWeight: "bold", color: "#666" }}>Action:</label>
              <span
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  fontSize: "13px",
                  fontWeight: "600",
                  display: "inline-block",
                  marginTop: "5px",
                  backgroundColor:
                    selectedLog.action_type === "CREATE"
                      ? "#d4edda"
                      : selectedLog.action_type === "UPDATE"
                        ? "#cfe2ff"
                        : selectedLog.action_type === "DELETE"
                          ? "#f8d7da"
                          : selectedLog.action_type === "LOGIN"
                            ? "#e7d4f5"
                            : selectedLog.action_type === "PASSWORD_RESET"
                              ? "#fff3cd"
                              : selectedLog.action_type === "PASSWORD_CHANGE"
                                ? "#e2e3e5"
                                : "#f0f0f0",
                  color:
                    selectedLog.action_type === "CREATE"
                      ? "#155724"
                      : selectedLog.action_type === "UPDATE"
                        ? "#084298"
                        : selectedLog.action_type === "DELETE"
                          ? "#721c24"
                          : selectedLog.action_type === "LOGIN"
                            ? "#531e8a"
                            : selectedLog.action_type === "PASSWORD_RESET"
                              ? "#856404"
                              : selectedLog.action_type === "PASSWORD_CHANGE"
                                ? "#383d41"
                                : "#666",
                }}
              >
                {selectedLog.action_type}
              </span>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ fontWeight: "bold", color: "#666" }}>Timestamp:</label>
              <p style={{ margin: "5px 0 0 0", color: "#333" }}>{new Date(selectedLog.created_at).toLocaleString()}</p>
            </div>

            {auditHeadline && (
              <div style={{ marginBottom: "15px" }}>
                <label style={{ fontWeight: "bold", color: "#666", display: "block", marginBottom: "8px" }}>{t.auditEvent}:</label>
                <div
                  style={{
                    backgroundColor: "#eef2ff",
                    border: "1px solid #c7d2fe",
                    borderRadius: "6px",
                    padding: "10px 12px",
                    color: "#1e3a8a",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {auditHeadline}
                </div>
              </div>
            )}

            {filteredDiffRows.length > 0 && (
              <div style={{ marginBottom: "15px" }}>
                <label style={{ fontWeight: "bold", color: "#666", display: "block", marginBottom: "10px" }}>
                  {parsedSelectedLogDiff.mode === "update"
                    ? t.changedFields
                    : parsedSelectedLogDiff.mode === "create"
                      ? t.createdFields
                      : t.deletedFields}
                  :
                </label>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: "6px", overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f8fafc" }}>
                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #e5e7eb", width: "28%" }}>{t.field}</th>
                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #e5e7eb", width: "36%" }}>{t.beforeValue}</th>
                        <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #e5e7eb", width: "36%" }}>{t.afterValue}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDiffRows.map((row, index) => (
                        <tr key={`${row.field}-${index}`}>
                          <td style={{ padding: "10px", borderBottom: "1px solid #f1f5f9", fontWeight: 600, color: "#334155", verticalAlign: "top" }}>
                            {row.field}
                          </td>
                          <td
                            style={{
                              padding: "10px",
                              borderBottom: "1px solid #f1f5f9",
                              color: "#64748b",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              verticalAlign: "top",
                            }}
                          >
                            {renderDiffValue(parsedSelectedLogDiff.mode === "create" ? "-" : row.before || row.value || "(empty)")}
                          </td>
                          <td
                            style={{
                              padding: "10px",
                              borderBottom: "1px solid #f1f5f9",
                              color: "#0f172a",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              verticalAlign: "top",
                            }}
                          >
                            {renderDiffValue(parsedSelectedLogDiff.mode === "delete" ? "-" : row.after || row.value || "(empty)")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowDetailsModal(false)}
              style={{
                width: "100%",
                padding: "10px",
                backgroundColor: "#224191",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsBoard;
